import React, { useContext, useEffect, useMemo, useState } from "react";
import { Card, Col, Grid, Row, Select, Skeleton, Space, Statistic, Typography, Avatar, Tag, Table } from "antd";
import {
  BarChartOutlined,
  CalendarOutlined,
  HomeOutlined,
  PieChartOutlined,
  UserOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Pie } from "react-chartjs-2";
import apiPath from "../../constants/apiPath";
import useRequest from "../../hooks/useRequest";
import { AuthContext } from "../../context/AuthContext";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const { useBreakpoint } = Grid;
const { Option } = Select;
const { Title: TTitle, Text } = Typography;

const theme = {
  primary: "var(--theme1, #eca413)",
  primaryDark: "var(--theme1Dark, #132272)",
};

const airlineStatsTemplate = {
  activeBookings: 0,
  pendingRequests: 0,
  totalHotels: 0,
  totalPassengersRegistered: 0,
  totalRooms: 0,
  usedRooms: 0,
  remainingRooms: 0,
  roomUsage: {
    confirmed: 0,
    pending: 0,
    cancelled: 0,
    totalRoomsConfirmed: 0,
  },
  hotelAllocations: [],
};

const airlineMetricCards = [
  { key: "activeBookings", title: "Active Bookings", icon: <CheckCircleOutlined />, color: "#52c41a", bg: "#f6ffed", path: "/bookings" },
  { key: "totalPassengersRegistered", title: "Passengers Registered", icon: <UserOutlined />, color: "#1890ff", bg: "#e6f7ff", path: "/admin/guest" },
  { key: "usedRooms", title: "Rooms Used", icon: <PieChartOutlined />, color: "#eb2f96", bg: "#fff0f6", path: "/hotel-management" },
  { key: "remainingRooms", title: "Rooms Remaining", icon: <HomeOutlined />, color: "#722ed1", bg: "#f9f0ff", path: "/hotel-management" },
  { key: "totalHotels", title: "Associated Hotels", icon: <HomeOutlined />, color: "#faad14", bg: "#fffbe6", path: "/hotel-management" },
];

const AirlineDashboard = () => {
  const navigate = useNavigate();
  const { request } = useRequest();
  const { userProfile } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(airlineStatsTemplate);
  const [chartType, setChartType] = useState("pie");
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState("all");
  const screens = useBreakpoint();

  const chartDataValues = useMemo(() => [
    stats.roomUsage?.confirmed || 0,
    stats.roomUsage?.pending || 0,
    stats.roomUsage?.cancelled || 0,
  ], [stats.roomUsage]);

  const chartLabels = ["Confirmed", "Pending", "Cancelled"];
  const chartColors = ["#52c41a", "#faad14", "#ff4d4f"];

  const barData = useMemo(
    () => ({
      labels: chartLabels,
      datasets: [
        {
          label: "Room Usage",
          data: chartDataValues,
          backgroundColor: chartColors,
          borderRadius: screens.xs ? 8 : 10,
          borderSkipped: false,
          maxBarThickness: screens.xs ? 36 : 52,
        },
      ],
    }),
    [chartDataValues, screens.xs]
  );

  const pieData = useMemo(
    () => ({
      labels: chartLabels,
      datasets: [
        {
          data: chartDataValues,
          backgroundColor: chartColors,
          borderColor: "#fff",
          borderWidth: 2,
          hoverOffset: 10,
        },
      ],
    }),
    [chartDataValues]
  );

  const barOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { backgroundColor: "rgba(0,0,0,0.85)", cornerRadius: 10, padding: 10 },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: "rgba(0,0,0,0.05)" },
          ticks: { color: "#7d7d7d", font: { size: screens.xs ? 10 : 12 } },
        },
        x: {
          grid: { display: false },
          ticks: { color: "#7d7d7d", font: { size: screens.xs ? 9 : 11 }, maxRotation: 0 },
        },
      },
    }),
    [screens.xs]
  );

  const pieOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: screens.xs ? "bottom" : "right",
          labels: { usePointStyle: true, pointStyle: "circle", padding: screens.xs ? 10 : 16 },
        },
      },
    }),
    [screens.xs]
  );

  const allocationColumns = [
    { title: "Hotel Name", dataIndex: "hotelName", key: "hotelName", render: (text) => <Text strong>{text}</Text> },
    { title: "Hotel ID", dataIndex: "hotelId", key: "hotelId", render: (text) => <Tag color="blue">{text}</Tag> },
    { title: "Bookings", dataIndex: "bookingCount", key: "bookingCount", align: "center", render: (val) => <Tag color="cyan">{val}</Tag> },
    { title: "Guests", dataIndex: "guestCount", key: "guestCount", align: "center", render: (val) => <Tag color="purple">{val}</Tag> },
    // { title: "Rooms", dataIndex: "roomCount", key: "roomCount", align: "center", render: (val) => <Tag color="orange">{val}</Tag> },
  ];

  useEffect(() => {
    setLoading(true);
    let url = `${apiPath.baseURL}${apiPath.airlineDashboardStats}`;
    if (selectedHotel !== "all") {
      url += `?hotel_id=${selectedHotel}`;
    }

    request({
      url,
      method: "GET",
      onSuccess: (res) => {
        if (res?.success && res.stats) {
          const s = res.stats;
          if (s.hotels) setHotels(s.hotels);
          setStats({
            ...s,
            totalRooms: s.room_summary?.total || 0,
            usedRooms: s.room_summary?.used || 0,
            remainingRooms: s.room_summary?.remaining || 0,
          });
        }
        setLoading(false);
      },
      onError: () => {
        setLoading(false);
      },
    });
  }, [request, selectedHotel]);

  return (
    <div className="dashboard-page">
      <Card
        bordered={false}
        style={{ borderRadius: 14, marginBottom: 14, background: "transparent", border: "none", boxShadow: "0 8px 24px rgba(19,34,114,0.08)", position: "relative", overflow: "hidden" }}
        bodyStyle={{ padding: 2, background: "transparent", border: "none" }}
      >
        <TTitle level={screens.xs ? 4 : 3} style={{ margin: 0, fontWeight: 700 }}>Airline Dashboard</TTitle>
        <Space direction="vertical" size={0}>
          <Text type="secondary">Real-time monitoring of your airline's operations and hotel partners.</Text>
          {!loading && (
            <Select
              placeholder="Select Hotel"
              value={selectedHotel}
              onChange={setSelectedHotel}
              style={{ width: screens.xs ? "100%" : 260, marginTop: 10 }}
            >
              <Option value="all">All Hotels</Option>
              {hotels.map(h => (
                <Option key={h.Hotel_Id} value={h.Hotel_Id}>{h.Hotel_name}</Option>
              ))}
            </Select>
          )}
        </Space>
      </Card>

      {/* <Card bordered={false} style={{ borderRadius: 14, marginBottom: 14, boxShadow: "0 8px 24px rgba(19,34,114,0.08)" }} bodyStyle={{ padding: screens.xs ? 14 : 18 }}>
        <Row gutter={[12, 10]} align="middle">
          <Col xs={24} md={3} style={{ textAlign: screens.xs ? "center" : "left" }}>
            <Avatar size={screens.xs ? 62 : 72} src={userProfile?.profileImage} icon={<UserOutlined />} style={{ border: `3px solid ${theme.primary}` }} />
          </Col>
          <Col xs={24} md={21}>
            <Space direction="vertical" size={2}>
              <Text strong style={{ fontSize: 18 }}>{userProfile?.name || "Airline Admin"}</Text>
              <Tag color="gold">{userProfile?.userType || "Airline"}</Tag>
              <Text type="secondary">{userProfile?.email}</Text>
            </Space>
          </Col>
        </Row>
      </Card> */}

      <Row gutter={[12, 12]}>
        {airlineMetricCards.map((card) => (
          <Col xs={24} sm={12} lg={8} key={card.key}>
            <Card
              bordered={false}
              hoverable
              onClick={() => {
                if (card.key === "usedRooms" || card.key === "remainingRooms") {
                  navigate(`${card.path}?view=${card.key}`);
                } else {
                  navigate(card.path);
                }
              }}
              style={{ borderRadius: 14, boxShadow: "0 8px 24px rgba(19,34,114,0.08)", background: `#fff`, height: "100%" }}
              bodyStyle={{ padding: screens.xs ? 14 : 18 }}
            >
              <Skeleton active loading={loading} paragraph={{ rows: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                  <div style={{ width: 42, height: 42, borderRadius: "50%", backgroundColor: card.bg, display: "flex", alignItems: "center", justifyContent: "center", color: card.color, fontSize: 18 }}>{card.icon}</div>
                  <Text type="secondary" style={{ fontWeight: 600 }}>{card.title}</Text>
                </div>
                <Statistic value={stats[card.key] ?? 0} valueStyle={{ color: card.color, fontSize: screens.xs ? 24 : 30, fontWeight: 800 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>Current {card.title.toLowerCase()}</Text>
              </Skeleton>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[14, 14]} style={{ marginTop: 14 }}>
        <Col xs={24} lg={10}>
          <Card bordered={false} style={{ borderRadius: 14, boxShadow: "0 8px 24px rgba(19,34,114,0.08)", height: "100%" }} bodyStyle={{ padding: screens.xs ? 14 : 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <Space direction="vertical" size={0}>
                <TTitle level={5} style={{ margin: 0 }}>Room Usage Analysis</TTitle>
                <Text type="secondary" style={{ fontSize: 12 }}>Breakdown of booking statuses</Text>
              </Space>
              <Select size="small" value={chartType} onChange={setChartType} style={{ width: 110 }}>
                <Option value="bar">Bar</Option>
                <Option value="pie">Pie</Option>
              </Select>
            </div>
            <Skeleton active loading={loading} paragraph={{ rows: 8 }}>
              <div style={{ height: 300, background: "#fff" }}>
                {chartType === "bar" ? <Bar data={barData} options={barOptions} /> : <Pie data={pieData} options={pieOptions} />}
              </div>
              <div style={{ marginTop: 10, textAlign: "center" }}>
                {/* <Text type="secondary" style={{ fontSize: 12 }}>Total Confirmed Rooms: <Text strong>{stats.roomUsage?.totalRoomsConfirmed || 0}</Text></Text> */}
              </div>
            </Skeleton>
          </Card>
        </Col>

        <Col xs={24} lg={14}>
          <Card bordered={false} style={{ borderRadius: 14, boxShadow: "0 8px 24px rgba(19,34,114,0.08)", height: "100%" }} bodyStyle={{ padding: screens.xs ? 14 : 18 }}>
            <Space direction="vertical" size={0} style={{ marginBottom: 14 }}>
              <TTitle level={5} style={{ margin: 0 }}>Hotel Partner Allocations</TTitle>
              <Text type="secondary" style={{ fontSize: 12 }}>Booking distribution across partner hotels</Text>
            </Space>
            <Skeleton active loading={loading} paragraph={{ rows: 8 }}>
              <Table
                dataSource={stats.hotelAllocations}
                columns={allocationColumns}
                pagination={{ pageSize: 5 }}
                size="small"
                rowKey="hotelId"
                scroll={{ x: true }}
              />
            </Skeleton>
          </Card>
        </Col>
      </Row>

      <style>{`
        .dashboard-page .ant-select-selector {
          border-radius: 8px !important;
        }
        .dashboard-page .ant-table {
          background: transparent;
        }
        .dashboard-page .ant-table-thead > tr > th {
          background: #f8fafc;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

export default AirlineDashboard;
